import React, { useState, useEffect } from 'react';
import { formatCurrency } from '../utils/formatCurrency';
import Navigation from './Navigation';
import SettingsModal from './SettingsModal';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db, auth, functions } from '../firebase/config';
import { httpsCallable } from 'firebase/functions';

function SettingsPage({ onLogout, onNavigate, userName, onAddPerson, people, onUpdatePerson, onDeletePerson }) {
  const [showModal, setShowModal] = useState(false);
  const [editingPerson, setEditingPerson] = useState(null);
  const [activeTab, setActiveTab] = useState('email'); // Tab state
  
  // Email Integration State
  const [emailConfig, setEmailConfig] = useState({
    server: 'mail.spacemail.com',
    port: 993,
    email: 'message@gentlehug.store',
    password: ''
  });
  const [emailConfigSaved, setEmailConfigSaved] = useState(false);
  const [emailTestResult, setEmailTestResult] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);

  // Load email config on mount
  useEffect(() => {
    loadEmailConfig();
  }, []);

  const loadEmailConfig = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;
      
      const configDoc = await getDoc(doc(db, 'emailConfig', user.uid));
      if (configDoc.exists()) {
        const data = configDoc.data();
        setEmailConfig({
          server: data.server || 'mail.spacemail.com',
          port: data.port || 993,
          email: data.email || 'message@gentlehug.store',
          password: '' // Never load password
        });
        setEmailConfigSaved(true);
      }
    } catch (error) {
      console.error('Error loading email config:', error);
    }
  };

  const saveEmailConfig = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        alert('Please login first');
        return;
      }

      setEmailLoading(true);
      
      // Eğer password boşsa ve daha önce kaydedilmişse, eski şifreyi koru
      const updateData = {
        server: emailConfig.server,
        port: emailConfig.port,
        email: emailConfig.email,
        updatedAt: new Date().toISOString()
      };
      
      // Sadece yeni şifre girildiyse güncelle
      if (emailConfig.password && emailConfig.password.trim() !== '') {
        updateData.password = emailConfig.password;
      } else if (!emailConfigSaved) {
        // İlk kayıtta şifre zorunlu
        alert('Password is required for first-time configuration');
        setEmailLoading(false);
        return;
      }
      
      await setDoc(doc(db, 'emailConfig', user.uid), updateData, { merge: true });

      setEmailConfigSaved(true);
      setEmailTestResult('✅ Configuration saved successfully!');
      
      // Password field'ını temizle
      setEmailConfig(prev => ({ ...prev, password: '' }));
      
      setTimeout(() => setEmailTestResult(''), 3000);
    } catch (error) {
      console.error('Error saving email config:', error);
      setEmailTestResult('❌ Failed to save configuration');
    } finally {
      setEmailLoading(false);
    }
  };

  const testEmailConnection = async () => {
    try {
      setEmailLoading(true);
      setEmailTestResult('⏳ Testing connection...');

      const testConnection = httpsCallable(functions, 'testEmailConnection');
      const result = await testConnection();

      if (result.data.success) {
        setEmailTestResult(`✅ ${result.data.message}`);
      } else {
        setEmailTestResult(`❌ Connection failed`);
      }
    } catch (error) {
      console.error('Test connection error:', error);
      setEmailTestResult(`❌ Error: ${error.message}`);
    } finally {
      setEmailLoading(false);
      setTimeout(() => setEmailTestResult(''), 5000);
    }
  };

  const checkEmailsNow = async () => {
    try {
      setEmailLoading(true);
      setEmailTestResult('⏳ Checking emails...');

      const checkEmails = httpsCallable(functions, 'checkEmailsNow');
      const result = await checkEmails();

        if (result.data.success) {
        const details = result.data.details || [];
        let message = result.data.message;
        
        if (details.length > 0) {
          message += '\n\n' + details.map(d => 
            `✅ ${d.storeName}: $${formatCurrency(d.amount)}`
          ).join('\n');
        }
        
        setEmailTestResult(message);
      } else {
        setEmailTestResult('❌ No emails found');
      }
    } catch (error) {
      console.error('Check emails error:', error);
      setEmailTestResult(`❌ Error: ${error.message}`);
    } finally {
      setEmailLoading(false);
      setTimeout(() => setEmailTestResult(''), 8000);
    }
  };

  const handleSavePerson = (personData) => {
    if (editingPerson) {
      onUpdatePerson(personData);
      setEditingPerson(null);
    } else {
      onAddPerson(personData);
    }
    setShowModal(false);
    alert(`Person #${personData.code} saved successfully!`);
  };

  const handleEditPerson = (person) => {
    setEditingPerson(person);
    setShowModal(true);
  };

  const handleDeletePerson = (person) => {
    if (window.confirm(`Are you sure you want to delete ${person.name} (#${person.code})? This action cannot be undone.`)) {
      onDeletePerson(person.code);
      alert(`Person #${person.code} deleted successfully!`);
    }
  };

  return (
    <div>
      <Navigation 
        currentPage="settings" 
        onNavigate={onNavigate}
        onLogout={onLogout}
        userName={userName}
      />
      
      <div className="container">
        <div className="settings-header-modern">
          <div className="settings-header-left">
            <h1 className="settings-title-modern">⚙️ Settings</h1>
            <p className="settings-subtitle">Manage your account and preferences</p>
          </div>
          <button className="btn-add-person-modern" onClick={() => { setEditingPerson(null); setShowModal(true); }}>
            <span className="btn-icon">➕</span>
            Add New Person
          </button>
        </div>

        {/* Tabs Navigation */}
        <div className="settings-tabs">
          <div 
            className={`settings-tab ${activeTab === 'email' ? 'active' : ''}`}
            onClick={() => setActiveTab('email')}
          >
            <span className="tab-icon">📧</span>
            <span className="tab-text">Email Integration</span>
            {emailConfigSaved && <span className="tab-badge">✓</span>}
          </div>
          <div 
            className={`settings-tab ${activeTab === 'people' ? 'active' : ''}`}
            onClick={() => setActiveTab('people')}
          >
            <span className="tab-icon">👥</span>
            <span className="tab-text">People</span>
            <span className="tab-count">{people?.length || 0}</span>
          </div>
          <div 
            className={`settings-tab ${activeTab === 'security' ? 'active' : ''}`}
            onClick={() => setActiveTab('security')}
          >
            <span className="tab-icon">🔐</span>
            <span className="tab-text">Security</span>
          </div>
        </div>

        {/* Tab Content */}
        <div className="settings-tab-content">
          {activeTab === 'email' && (
            <div className="tab-panel">
              <div className="panel-header">
                <h3 className="panel-title">Email Integration</h3>
                {emailConfigSaved && <span className="status-badge-modern">✅ Configured</span>}
              </div>
              <p className="panel-desc">Automatically import Etsy payments from forwarded emails</p>

              <div className="email-config-card">
                <div className="form-row">
                  <div className="form-group-modern">
                    <label>IMAP Server</label>
                    <input
                      type="text"
                      value={emailConfig.server}
                      onChange={(e) => setEmailConfig({...emailConfig, server: e.target.value})}
                      placeholder="imap.purelymail.com"
                      disabled={emailLoading}
                      className="input-modern"
                    />
                  </div>
                  <div className="form-group-modern" style={{maxWidth: '140px'}}>
                    <label>Port</label>
                    <input
                      type="number"
                      value={emailConfig.port}
                      onChange={(e) => setEmailConfig({...emailConfig, port: e.target.value})}
                      disabled={emailLoading}
                      className="input-modern"
                    />
                  </div>
                </div>

                <div className="form-group-modern">
                  <label>Email Address</label>
                  <input
                    type="email"
                    value={emailConfig.email}
                    onChange={(e) => setEmailConfig({...emailConfig, email: e.target.value})}
                    placeholder="your-email@domain.com"
                    disabled={emailLoading}
                    className="input-modern"
                  />
                </div>

                <div className="form-group-modern">
                  <label>
                    Password 
                    {emailConfigSaved && <span className="label-badge">Saved</span>}
                  </label>
                  <input
                    type="password"
                    value={emailConfig.password}
                    onChange={(e) => setEmailConfig({...emailConfig, password: e.target.value})}
                    placeholder={emailConfigSaved ? "Leave empty to keep current password" : "Enter email password"}
                    disabled={emailLoading}
                    className="input-modern"
                  />
                  <small className="input-hint">🔒 Password is stored securely in Firebase</small>
                </div>

                {emailTestResult && (
                  <div className={`alert-modern ${emailTestResult.includes('✅') ? 'success' : emailTestResult.includes('❌') ? 'error' : 'info'}`}>
                    <div className="alert-content">{emailTestResult}</div>
                  </div>
                )}

                <div className="button-group-modern">
                  <button
                    className="btn-primary-modern"
                    onClick={saveEmailConfig}
                    disabled={emailLoading}
                  >
                    <span className="btn-icon">💾</span>
                    Save Configuration
                  </button>
                  <button
                    className="btn-secondary-modern"
                    onClick={testEmailConnection}
                    disabled={!emailConfigSaved || emailLoading}
                  >
                    <span className="btn-icon">🔍</span>
                    Test Connection
                  </button>
                  <button
                    className="btn-secondary-modern success"
                    onClick={checkEmailsNow}
                    disabled={!emailConfigSaved || emailLoading}
                  >
                    <span className="btn-icon">📬</span>
                    Check Emails Now
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'people' && (
            <div className="tab-panel">
              <div className="panel-header">
                <h3 className="panel-title">People Management</h3>
                <span className="panel-count">{people?.length || 0} people</span>
              </div>
              <p className="panel-desc">Add, edit, and manage people and their stores</p>

              {people && people.length > 0 ? (
                <div className="people-management-grid">
                  {people
                    .sort((a, b) => a.code.localeCompare(b.code))
                    .map(person => {
                      const totalBalance = person.stores?.reduce((sum, s) => sum + s.bal, 0) || 0;
                      const activeStores = person.stores?.filter(s => s.email && s.email.trim() !== '').length || 0;
                      
                      return (
                        <div key={person.id} className="management-person-card">
                          <div className="management-card-header">
                            <div className="management-person-code">#{person.code}</div>
                            <div className="management-card-actions">
                              <button className="btn-icon-action edit" onClick={() => handleEditPerson(person)} title="Edit">
                                ✏️
                              </button>
                              <button className="btn-icon-action delete" onClick={() => handleDeletePerson(person)} title="Delete">
                                🗑️
                              </button>
                            </div>
                          </div>
                          <div className="management-person-name">{person.name}</div>
                          <div className="management-person-stats">
                            <div className="management-stat">
                              <span className="stat-value">${formatCurrency(totalBalance)}</span>
                              <span className="stat-label">Balance</span>
                            </div>
                            <div className="management-stat">
                              <span className="stat-value">{person.stores?.length || 0}</span>
                              <span className="stat-label">Stores</span>
                            </div>
                            <div className="management-stat">
                              <span className="stat-value">{activeStores}</span>
                              <span className="stat-label">Active</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <div className="empty-state-modern">
                  <div className="empty-icon-modern">📋</div>
                  <div className="empty-text-modern">No people added yet</div>
                  <div className="empty-desc-modern">Click "Add New Person" to get started</div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'security' && (
            <div className="tab-panel">
              <div className="panel-header">
                <h3 className="panel-title">Security & Privacy</h3>
                <span className="status-badge-modern success">✅ Secure</span>
              </div>
              <p className="panel-desc">Your data is protected with industry-standard security</p>

              <div className="security-features">
                <div className="security-feature-card">
                  <div className="feature-icon">🔐</div>
                  <div className="feature-content">
                    <h4 className="feature-title">Two-Factor Authentication</h4>
                    <p className="feature-desc">TOTP-based 2FA is enabled for your account</p>
                  </div>
                  <div className="feature-status enabled">Enabled</div>
                </div>

                <div className="security-feature-card">
                  <div className="feature-icon">🔒</div>
                  <div className="feature-content">
                    <h4 className="feature-title">Data Encryption</h4>
                    <p className="feature-desc">All data is encrypted at rest in Firebase</p>
                  </div>
                  <div className="feature-status enabled">Active</div>
                </div>

                <div className="security-feature-card">
                  <div className="feature-icon">🛡️</div>
                  <div className="feature-content">
                    <h4 className="feature-title">Firestore Security Rules</h4>
                    <p className="feature-desc">Database access is restricted to authorized users only</p>
                  </div>
                  <div className="feature-status enabled">Active</div>
                </div>

                <div className="security-feature-card">
                  <div className="feature-icon">💾</div>
                  <div className="feature-content">
                    <h4 className="feature-title">Auto Backup</h4>
                    <p className="feature-desc">Your data is automatically backed up to Firebase Cloud</p>
                  </div>
                  <div className="feature-status enabled">Active</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <SettingsModal 
          onClose={() => { setShowModal(false); setEditingPerson(null); }}
          onSave={handleSavePerson}
          editPerson={editingPerson}
        />
      )}
    </div>
  );
}

export default SettingsPage;